import { useMemo, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';
import { ChartBox, chartDataset } from '../components/ChartBox';
import { FilterSelect } from '../components/FilterSelect';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { FunnelRecord, FunnelResponse, ScorecardRow } from '../types';
import { asNumber, exportCsv, money, num, pct, uniq } from '../utils';
import { careStatusLabel, careStatusTone, contactTone, FUNNEL_STAGES, FUNNEL_SUPPORT, STAGE_TONE } from '../lib/identity';

const stageDefs = FUNNEL_STAGES;
const supportDefs = FUNNEL_SUPPORT;

const stageMap = Object.fromEntries(stageDefs.map(stage => [stage.id, stage]));
const supportMap = Object.fromEntries(supportDefs.map(status => [status.id, status]));

// converted_30d (funnel.js) gồm cả đơn đúng ngày ghi nhận — thực chất mua tại quầy chưa gắn cờ.
// Khi API trả converted_followup/converted_same_day thì tách hai dòng; chưa có thì ghi rõ nhãn gộp.
const CONVERTED_ALL_LABEL = 'Có đơn ≤30d (gồm cùng ngày)';
const CONVERTED_FOLLOWUP_LABEL = 'Đã chốt ≤ 30d';
const SAME_DAY_LABEL = 'Đơn cùng ngày';

interface FunnelScreenProps {
  data: FunnelResponse | null;
  loaded: boolean;
  busy: boolean;
}

export function FunnelScreen({ data, loaded, busy }: FunnelScreenProps) {
  const [filters, setFilters] = useState({ store: '', stage: '', support: '', contact: '', chat: '', month: '', q: '' });
  const records = data?.records || [];
  const scorecard = data?.scorecard || [];
  const stores = uniq([...scorecard.map(row => row.store_code), ...records.map(row => row.store_code)]);
  const months = uniq(records.map(row => row.period || ''));
  const setFilter = (patch: Partial<typeof filters>) => setFilters(current => ({ ...current, ...patch }));
  const reset = () => setFilters({ store: '', stage: '', support: '', contact: '', chat: '', month: '', q: '' });

  const scopeRows = useMemo(() => records.filter(row => {
    const haystack = `${row.capture_id || ''} ${row.customer_name || ''} ${row.phone || ''} ${row.store_code || ''} ${row.store_care_status || ''} ${row.need_type || ''}`.toLowerCase();
    return (!filters.store || row.store_code === filters.store)
      && (!filters.month || row.period === filters.month)
      && (!filters.q || haystack.includes(filters.q.toLowerCase()));
  }), [records, filters.store, filters.month, filters.q]);

  const filteredRows = useMemo(() => scopeRows.filter(row =>
    (!filters.stage || row.funnel_stage === filters.stage)
    && (!filters.support || row.support_status === filters.support)
    && (!filters.contact || (filters.contact === 'has_phone' ? row.has_phone : !row.has_phone))
    && (!filters.chat || (filters.chat === 'has_chat' ? row.has_chat : !row.has_chat))
  ), [scopeRows, filters.stage, filters.support, filters.contact, filters.chat]);

  const hasFollowup = records.some(row => typeof row.converted_followup === 'boolean');
  const trendRows = useMemo(() => {
    const map = new Map<string, { period: string; captures: number; contactable: number; missing_contact: number; with_chat: number; converted_30d: number; converted_followup: number; converted_same_day: number }>();
    scopeRows.forEach(row => {
      const key = row.period || 'Chưa rõ';
      const current = map.get(key) || { period: key, captures: 0, contactable: 0, missing_contact: 0, with_chat: 0, converted_30d: 0, converted_followup: 0, converted_same_day: 0 };
      current.captures += 1;
      if (row.has_phone) current.contactable += 1; else current.missing_contact += 1;
      if (row.has_chat) current.with_chat += 1;
      if (row.converted_30d) current.converted_30d += 1;
      if (row.converted_followup) current.converted_followup += 1;
      if (row.converted_same_day) current.converted_same_day += 1;
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => a.period.localeCompare(b.period));
  }, [scopeRows]);

  const stageCounts = stageDefs.map(stage => ({ ...stage, count: scopeRows.filter(row => row.funnel_stage === stage.id).length }));
  const supportCounts = supportDefs.map(status => ({ ...status, count: scopeRows.filter(row => row.support_status === status.id).length }));
  const maxStage = Math.max(...stageCounts.map(stage => stage.count), 1);
  const maxSupport = Math.max(...supportCounts.map(status => status.count), 1);
  const scoreRows = filters.store ? scorecard.filter(row => row.store_code === filters.store) : scorecard;
  const palexyVisits = scoreRows.reduce((sum, row) => sum + asNumber(row.visits), 0);
  const fSalesBuyers = scoreRows.reduce((sum, row) => sum + asNumber(row.buyers), 0);
  const fSalesRevenue = scoreRows.reduce((sum, row) => sum + asNumber(row.revenue), 0);
  const totals = {
    captures: scopeRows.length,
    visible: filteredRows.length,
    contactable: scopeRows.filter(row => row.has_phone).length,
    missing: scopeRows.filter(row => !row.has_phone).length,
    withChat: scopeRows.filter(row => row.has_chat).length,
    ready: scopeRows.filter(row => ['ready_followup', 'post_sale_care'].includes(row.support_status || '')).length,
  };
  const contactRate = totals.captures ? totals.contactable * 100 / totals.captures : 0;

  if (busy && !loaded) return <div className="card empty">Đang tải phễu chuyển đổi...</div>;
  if (!loaded) return <div className="card empty">Mở Phễu chuyển đổi để tải dữ liệu.</div>;

  return (
    <>
      <section className="grid kpis">
        <KpiCard label="Lượt khách đến cửa hàng" value={num(palexyVisits)} sub={filters.store || 'Toàn hệ thống'} tone={STAGE_TONE.V} />
        <KpiCard label="Lượt tư vấn ghi nhận" value={num(totals.captures)} sub={`${num(totals.visible)} lượt đang lọc`} />
        <KpiCard label="Collect SĐT" value={pct(contactRate)} sub={`${num(totals.contactable)} có SĐT · ${num(totals.missing)} thiếu`} tone={contactTone(contactRate)} />
        <KpiCard label="Support ready" value={num(totals.ready)} sub="Sẵn chăm sau bán/follow" tone="green" />
        <KpiCard label="Hội thoại đã đối soát" value={num(totals.withChat)} sub="Lượt tư vấn có hội thoại" tone={STAGE_TONE.T} />
        <KpiCard label="f_sales buyers" value={num(fSalesBuyers)} sub={`${money(fSalesRevenue)} doanh thu store`} tone={STAGE_TONE.F} />
        {hasFollowup && <KpiCard label={CONVERTED_FOLLOWUP_LABEL} value={num(scopeRows.filter(row => row.converted_followup).length)} sub="Đơn ≥ D+1 sau ngày ghi nhận" tone={STAGE_TONE.O} />}
        {hasFollowup && <KpiCard label={SAME_DAY_LABEL} value={num(scopeRows.filter(row => row.converted_same_day).length)} sub="Mua ngay ngày ghi nhận — chưa gắn bán tại quầy" tone="amber" />}
      </section>

      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Store</span>
          <button className={`tab ${!filters.store ? 'active' : ''}`} onClick={() => setFilter({ store: '' })} type="button">Tất cả</button>
          {stores.map(store => <button key={store} className={`tab ${filters.store === store ? 'active' : ''}`} onClick={() => setFilter({ store })} type="button">{store}</button>)}
        </div>
        <div className="filter-grid">
          <div className="control"><label htmlFor="funnel-search">Tìm khách hàng</label><input id="funnel-search" value={filters.q} onChange={event => setFilter({ q: event.target.value })} placeholder="Tên khách, SĐT, trạng thái..." /></div>
          <FilterSelect label="Tháng" value={filters.month} onChange={month => setFilter({ month })} options={months} />
          <FilterSelect label="Liên hệ" value={filters.contact} onChange={contact => setFilter({ contact })} options={['has_phone', 'no_phone']} />
          <FilterSelect label="Chat" value={filters.chat} onChange={chat => setFilter({ chat })} options={['has_chat', 'no_chat']} />
        </div>
        <div className="actions start">
          <button aria-label="Xóa bộ lọc funnel" className="btn" onClick={reset} type="button"><RotateCcw size={15} />Xóa lọc</button>
          <button aria-label="Xuất danh sách phễu chuyển đổi" className="btn primary" onClick={() => exportCsv(filteredRows, { store: scorecard as ScorecardRow[], staff: stageCounts }, 'Care_CDP_Funnel_Record')} type="button"><Download size={15} />Xuất danh sách</button>
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Xu hướng tiếp cận, tư vấn và mua hàng</h2><span>Chọn tháng để lọc danh sách bên dưới</span></div>
          <div className="chart">
            <ChartBox labels={trendRows.map(row => row.period)} datasets={[
              chartDataset('Capture', trendRows.map(row => row.captures), '#2563eb'),
              chartDataset('Có SĐT', trendRows.map(row => row.contactable), '#059669', 'line'),
              chartDataset('Có chat', trendRows.map(row => row.with_chat), '#7c3aed', 'line'),
              ...(hasFollowup
                ? [
                  chartDataset(CONVERTED_FOLLOWUP_LABEL, trendRows.map(row => row.converted_followup), '#d71920', 'line'),
                  chartDataset(SAME_DAY_LABEL, trendRows.map(row => row.converted_same_day), '#d97706', 'line'),
                ]
                : [chartDataset(CONVERTED_ALL_LABEL, trendRows.map(row => row.converted_30d), '#d71920', 'line')]),
            ]} onPointClick={({ label }) => setFilter({ month: filters.month === label ? '' : label })} />
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Stage cross-filter</h2><span>{filters.stage ? stageMap[filters.stage]?.label : 'Đang xem mọi stage'}</span></div>
          <div className="stage-list">
            {stageCounts.map(stage => (
              <button aria-label={`Lọc stage ${stage.label}`} key={stage.id} className={`stage-button ${filters.stage === stage.id ? 'active' : ''}`} onClick={() => setFilter({ stage: filters.stage === stage.id ? '' : stage.id })} type="button">
                <div className="stage-top"><span>{stage.label}</span><Tag tone={stage.tone}>{num(stage.count)}</Tag></div>
                <div className="bar"><i style={{ width: `${Math.max(stage.count / maxStage * 100, stage.count ? 2 : 0)}%`, background: stage.color }} /></div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Lượt khách, lượt tư vấn và đơn hàng</h2><span>Chọn cửa hàng để lọc</span></div>
          <div className="scroll">
            <table>
              <thead><tr><th>Cửa hàng</th><th className="right">Lượt khách</th><th className="right">Lượt tư vấn</th><th className="right">Khách mua</th><th className="right">Mua / lượt khách</th><th className="right">Doanh thu</th></tr></thead>
              <tbody>{scorecard.map(row => (
                <tr key={row.store_code} className={`click-row ${filters.store === row.store_code ? 'active' : ''}`} onClick={() => setFilter({ store: filters.store === row.store_code ? '' : row.store_code })}>
                  <td><b>{row.store_code}</b><div className="muted">{row.store_name}</div></td>
                  <td className="right tnum">{num(row.visits)}</td>
                  <td className="right tnum">{num(row.lead_captures)}</td>
                  <td className="right tnum">{num(row.buyers)}</td>
                  <td className="right"><Tag tone={STAGE_TONE.F}>{pct(row.buyer_per_visit_pct)}</Tag></td>
                  <td className="right tnum">{money(row.revenue)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Trạng thái chăm sóc</h2><span>Chọn trạng thái để lọc</span></div>
          <div className="mini-bars">
            {supportCounts.map(status => (
              <div key={status.id} className="mini-bar">
                <button className={`tag ${filters.support === status.id ? 'red' : status.tone}`} onClick={() => setFilter({ support: filters.support === status.id ? '' : status.id })} type="button">{status.label}</button>
                <div className="mini-track"><div className="mini-fill" style={{ width: `${Math.max(status.count / maxSupport * 100, status.count ? 2 : 0)}%` }} /></div>
                <b className="tnum right">{num(status.count)}</b>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card">
        <div className="section-title"><h2>Chi tiết phễu chuyển đổi</h2><span>{num(filteredRows.length)} / {num(records.length)} lượt tư vấn</span></div>
        <div className="scroll tall">
          <table>
            <thead><tr><th>Ngày</th><th>Cửa hàng</th><th>Mã tư vấn</th><th>Khách</th><th>SĐT</th><th>Giai đoạn</th><th>Chăm sóc</th><th>Trạng thái</th><th>Hội thoại</th><th>{hasFollowup ? CONVERTED_FOLLOWUP_LABEL : CONVERTED_ALL_LABEL}</th><th>Tổng đơn</th><th>Doanh thu ≤30 ngày</th></tr></thead>
            <tbody>{filteredRows.slice(0, 800).map(row => <FunnelRecordRow key={row.capture_id} row={row} />)}</tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function FunnelRecordRow({ row }: { row: FunnelRecord }) {
  const stage = stageMap[row.funnel_stage || 'review'] || stageMap.review;
  const support = supportMap[row.support_status || 'review'] || supportMap.review;
  return (
    <tr>
      <td className="tnum muted">{row.reported_date}</td>
      <td><b>{row.store_code}</b></td>
      <td className="tnum">{row.capture_id}</td>
      <td>{row.customer_name || '-'}</td>
      <td className="tnum">{row.phone || '-'}</td>
      <td><Tag tone={stage.tone}>{stage.label}</Tag></td>
      <td><Tag tone={support.tone}>{support.label}</Tag></td>
      <td><Tag tone={careStatusTone(row.store_care_status)}>{careStatusLabel(row.store_care_status)}</Tag></td>
      <td>{row.has_chat ? <Tag tone={STAGE_TONE.T}>có chat</Tag> : <span className="muted">-</span>}</td>
      <td>
        {row.converted_followup ? <Tag tone={STAGE_TONE.O}>có</Tag>
          : row.converted_same_day ? <Tag tone="amber">{SAME_DAY_LABEL.toLowerCase()}</Tag>
          : row.converted_30d ? <Tag tone={STAGE_TONE.O}>có</Tag>
          : <span className="muted">-</span>}
      </td>
      <td>{row.lifetime_customer ? <Tag tone="blue">{num(row.lifetime_orders)} đơn</Tag> : <span className="muted">-</span>}</td>
      <td className="tnum right">{row.converted_30d ? money(row.rev_30d) : '-'}</td>
    </tr>
  );
}
